import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import StatCard from '../components/common/StatCard';
import Badge from '../components/common/Badge';
import Button from '../components/common/Button';
import Loader from '../components/common/Loader';
import { getStudentMe, getMyApplications, getSavedJobs, getFeaturedJobs } from '../api';

const Dashboard = () => {
  const [profile, setProfile] = useState(null);
  const [applications, setApplications] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const [profileRes, appsRes, jobsRes] = await Promise.all([
          getStudentMe(),
          getMyApplications(),
          getFeaturedJobs()
        ]);
        
        setProfile(profileRes.data);
        setApplications(appsRes.data || []);
        setJobs(jobsRes.data?.slice(0, 4) || []); // Get top 4
      } catch (err) {
        console.error('Failed to load dashboard', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchDashboardData();
  }, []);

  const calculateProfileCompletion = () => {
    if (!profile) return 0;
    const fields = ['full_name', 'college_name', 'branch', 'year', 'skills'];
    const filled = fields.filter(f => profile[f] && profile[f].length > 0).length;
    return Math.round((filled / fields.length) * 100);
  };

  const completionPercent = calculateProfileCompletion();

  if (loading) {
    return (
      <div className="page-wrapper">
        <Navbar />
        <div className="main-content flex justify-center items-center h-full"><Loader fullPage /></div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="page-wrapper dashboard-page">
      <Navbar />
      
      <div className="dashboard-header">
        <div className="container">
          <h1 className="dashboard-greeting">Welcome back, {profile?.full_name?.split(' ')[0] || 'Student'}! 👋</h1>
          <p className="dashboard-sub">{profile?.college_name}</p>
        </div>
      </div>
      
      <main className="container mt-8 flex-1">
        <div className="dashboard-grid">
          
          {/* Left Sidebar */}
          <div className="dashboard-sidebar">
            <div className="card text-center p-6">
              <div className="w-24 h-24 bg-primary-light text-primary rounded-full mx-auto flex items-center justify-center text-3xl font-display font-bold mb-4">
                {profile?.full_name?.charAt(0) || 'S'}
              </div>
              <h3 className="font-semi text-gray-900 text-lg">{profile?.full_name}</h3>
              <p className="text-sm text-gray-500 mb-4">{profile?.branch || 'Branch not set'}</p>
              
              <div className="text-left mt-4 border-t border-gray-100 pt-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-gray-700">Profile Completion</span>
                  <span className="text-sm font-bold text-primary">{completionPercent}%</span>
                </div>
                <div className="progress-bar-track">
                  <div className="progress-bar-fill" style={{ width: `${completionPercent}%` }}></div>
                </div>
                {completionPercent < 100 && (
                  <p className="text-xs text-gray-400 mt-2">Complete your profile to increase your chances of selection.</p>
                )}
              </div>
              
              <Button variant="outline" className="w-full mt-6" size="sm">Edit Profile</Button>
            </div>
            
            <div className="card">
              <h3 className="font-semi mb-4">Quick Links</h3>
              <div className="flex-col gap-2">
                <Link to="/browse" className="text-sm text-gray-600 hover:text-primary p-2 hover:bg-gray-50 rounded transition-colors flex items-center gap-2">
                  🔍 Find New Jobs
                </Link>
                <Link to="#" className="text-sm text-gray-600 hover:text-primary p-2 hover:bg-gray-50 rounded transition-colors flex items-center gap-2">
                  📄 My Resume
                </Link>
                <Link to="#" className="text-sm text-gray-600 hover:text-primary p-2 hover:bg-gray-50 rounded transition-colors flex items-center gap-2">
                  ⚙️ Settings
                </Link>
              </div>
            </div>
          </div>
          
          {/* Main Content */}
          <div className="dashboard-main">
            
            {/* Stats Row */}
            <div className="grid grid-3 gap-6">
              <StatCard title="Total Applications" value={applications.length} icon="📝" />
              <StatCard title="Interviews" value={applications.filter(a => a.status === 'Interview Scheduled').length} icon="🤝" />
              <StatCard title="Selected" value={applications.filter(a => a.status === 'Selected').length} icon="🎉" />
            </div>
            
            {/* Applications Table */}
            <div className="card">
              <div className="flex justify-between items-center mb-6">
                <h2 className="heading-3">Recent Applications</h2>
              </div>
              
              {applications.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">📄</div>
                  <h3 className="empty-title">No applications yet</h3>
                  <p className="empty-desc">Start applying to jobs and internships to see them track here.</p>
                  <Link to="/browse"><Button className="mt-4">Browse Jobs</Button></Link>
                </div>
              ) : (
                <div className="table-wrapper">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Job Title</th>
                        <th>Company</th>
                        <th>Applied On</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {applications.slice(0, 5).map(app => (
                        <tr key={app.id}>
                          <td className="font-medium text-gray-900">{app.opportunity?.title || 'Unknown Job'}</td>
                          <td>{app.opportunity?.company_name || '-'}</td>
                          <td>{new Date(app.created_at).toLocaleDateString()}</td>
                          <td>
                            <Badge variant={
                              app.status === 'Selected' ? 'success' :
                              app.status === 'Rejected' ? 'danger' :
                              app.status === 'Interview Scheduled' ? 'primary' :
                              app.status === 'Shortlisted' ? 'accent' : 'gray'
                            }>
                              {app.status}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            
            {/* Recommended Jobs */}
            <div>
              <div className="flex justify-between items-center mb-4">
                <h2 className="heading-3">Recommended for you</h2>
                <Link to="/browse" className="text-sm font-semi text-primary hover:underline">View All</Link>
              </div>
              <div className="grid grid-2 gap-4">
                {jobs.length > 0 ? jobs.map(job => (
                  <Link to={`/opportunity/${job.id}`} key={job.id} className="job-card group h-full">
                    <div className="job-card-header">
                      <div>
                        <h3 className="job-title group-hover:text-primary transition-colors">{job.title}</h3>
                        <div className="job-company">{job.company_name}</div>
                      </div>
                      <Badge variant="primary-light" className="flex-shrink-0">{job.employment_type || 'Job'}</Badge>
                    </div>
                    <div className="job-meta mt-auto pt-4">
                      <div className="job-meta-item">📍 {job.location || 'Remote'}</div>
                    </div>
                  </Link>
                )) : (
                  <div className="col-span-2 text-center py-8 text-gray-500 bg-white rounded-lg border border-gray-200">
                    No recommendations available right now.
                  </div>
                )}
              </div>
            </div>
            
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default Dashboard;
